import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
const toast = { success: (m) => console.log("[toast]", m), error: (m) => console.error("[toast]", m), info: (m) => console.log("[toast]", m) };
import PageContainer, { PageHeader } from "@/components/PageContainer";
import api from "@/lib/assessments";
import { readAll } from "@/lib/assessments";
const formatApiError = (e) => (e && e.message) || "Unknown error";
import { Save, ChevronLeft, ShieldAlert } from "lucide-react";
import { SAFET_STEPS, SAFET_LEVELS } from "@/data/instruments";

export default function SAFET() {
  const navigate = useNavigate();
  const [patient, setPatient] = useState("");
  const [notes, setNotes] = useState("");
  const [checked, setChecked] = useState({}); // {step_id-idx: bool}
  const [level, setLevel] = useState("");
  const [intervention, setIntervention] = useState("");
  const [saving, setSaving] = useState(false);

  const selectedLevel = SAFET_LEVELS.find((l) => l.value === level);
  const canSave = !!level;

  const save = async () => {
    if (!canSave) {
      toast.error("Choose an overall risk level before saving.");
      return;
    }
    setSaving(true);
    try {
      const structured = {};
      SAFET_STEPS.forEach((s) => {
        structured[s.id] = s.items.map((_, i) => !!checked[`${s.id}-${i}`]);
      });
      await api.post("/assessments", {
        kind: "safet",
        patient_identifier: patient || null,
        responses: { checked: structured, level, intervention },
        score: level === "high" ? 3 : level === "moderate" ? 2 : 1,
        interpretation: `${selectedLevel.label} — ${selectedLevel.detail.split(".")[0]}`,
        notes: notes || null,
      });
      toast.success("SAFE-T assessment saved.");
      navigate("/portal/continuum/tools/history");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer testid="safet-page">
      <Link to="/portal/continuum/tools" className="text-sm text-ink-muted hover:text-navy inline-flex items-center gap-1 mb-4" data-testid="safet-back">
        <ChevronLeft size={14} strokeWidth={2} /> All tools
      </Link>

      <PageHeader overline="Portal · Tool" title="SAFE-T Suicide Risk Assessment"
        subtitle="Five-step framework: identify risk factors, protective factors, ask about suicidal thoughts, determine risk level, document intervention." />

      <div className="card p-5 mb-6 bg-rust/10 border-rust/30" data-testid="safet-safety-warning">
        <div className="flex items-start gap-3">
          <ShieldAlert size={20} strokeWidth={2} className="text-rust flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-navy font-semibold mb-1">Clinical use only.</div>
            <p className="text-sm text-ink leading-relaxed">
              This tool documents an assessment — it does not replace clinical judgment. If a patient endorses
              active suicidal ideation with plan and intent, escalate immediately per your program's crisis
              protocol.
            </p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="card p-5">
            <label className="label">Patient identifier</label>
            <input className="field" value={patient} onChange={(e) => setPatient(e.target.value)} placeholder="Initials or MRN" data-testid="safet-patient" />
          </div>

          {SAFET_STEPS.map((step) => (
            <div key={step.id} className="card p-5" data-testid={`safet-step-${step.id}`}>
              <h3 className="text-navy font-semibold mb-3">{step.label}</h3>
              <ul className="space-y-2">
                {step.items.map((it, i) => {
                  const k = `${step.id}-${i}`;
                  return (
                    <li key={i}>
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          className="mt-1 accent-teal"
                          checked={!!checked[k]}
                          onChange={(e) => setChecked({ ...checked, [k]: e.target.checked })}
                          data-testid={`safet-${step.id}-${i}`}
                        />
                        <span className="text-sm text-ink">{it}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          <div className="card p-5" data-testid="safet-step-level">
            <h3 className="text-navy font-semibold mb-3">Step 4 — Determine overall risk level</h3>
            <div className="grid md:grid-cols-3 gap-2">
              {SAFET_LEVELS.map((l) => (
                <button
                  key={l.value}
                  onClick={() => setLevel(l.value)}
                  className={`text-left p-4 rounded-md border transition-colors ${
                    level === l.value ? "border-teal bg-teal-light/30" : "border-line hover:border-navy"
                  }`}
                  data-testid={`safet-level-${l.value}`}
                >
                  <div className="text-navy font-semibold">{l.label}</div>
                  <div className="text-xs text-ink-muted mt-1 leading-relaxed">{l.detail}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="card p-5" data-testid="safet-step-intervention">
            <h3 className="text-navy font-semibold mb-3">Step 5 — Intervention and documentation</h3>
            <textarea
              className="field min-h-[120px]"
              value={intervention}
              onChange={(e) => setIntervention(e.target.value)}
              placeholder="Safety plan, means restriction, next contact, crisis resources shared, involved supports, follow-up scheduled..."
              data-testid="safet-intervention"
            />
          </div>

          <div className="card p-5">
            <label className="label">Additional notes</label>
            <textarea className="field min-h-[80px]" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="safet-notes" />
          </div>
        </div>

        <aside className="lg:sticky lg:top-6 self-start">
          <div className="card p-6" data-testid="safet-summary">
            <div className="overline mb-2">Risk level</div>
            {selectedLevel ? (
              <>
                <div className={`badge mb-3 ${level === "high" ? "badge-rust" : level === "moderate" ? "badge-teal" : "badge-success"}`}>
                  {selectedLevel.label}
                </div>
                <p className="text-sm text-ink leading-relaxed">{selectedLevel.detail}</p>
              </>
            ) : (
              <div className="text-sm text-ink-muted">Complete steps 1–4 to determine risk level.</div>
            )}

            <div className="rule my-4" />
            <button onClick={save} disabled={!canSave || saving} className="btn btn-primary w-full justify-center" data-testid="safet-save">
              {saving ? "Saving..." : (<><Save size={14} strokeWidth={2} /> Save documentation</>)}
            </button>
          </div>
        </aside>
      </div>
    </PageContainer>
  );
}
